import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BodyBadge } from '@/components/BodyBadge';
import { Icon } from '@/components/Icon';
import { explainMeeting, fetchMeetingDetail } from '@/lib/api';
import { TranscriptPanel } from '@/features/meeting/TranscriptPanel';
import type { Citation, MeetingDetail as MeetingDetailData } from '@/lib/types';
import { can, isProTier } from '@/lib/tier';
import { useTier } from '@/session/SessionProvider';
import './MeetingDetail.css';

/**
 * Meeting detail, tier-adaptive.
 *
 *   consumer  plain-English summary and the Aware Explain button. Nothing else.
 *   pro       adds the structured rail — votes with their tallies, dollar figures pulled from
 *             the discussion, and what was named in the meeting.
 *
 * The rail does not disappear on a narrow screen; it stacks beneath the article. Hiding a
 * pro user's extractions because they picked up a phone would be losing data, not adapting.
 *
 * Every extracted fact carries a citation — the verbatim sentence it came from, plus its exact
 * character span in the transcript. Clicking one opens the transcript with that sentence
 * marked, so a claim can always be checked against the source.
 *
 * This is cite-to-passage, deliberately, NOT cite-to-timestamp. Per-item `start_time` is a
 * planned pipeline addition that does not exist yet; rendering a position the pipeline never
 * produced would be worse than citing the sentence. If `start_time` ships later, a video seek
 * can be added alongside this — it does not replace it.
 */
export function MeetingDetail({
  meetingId,
  onClose,
  closeLabel = 'Back to list',
  position,
  onPrev,
  onNext,
}: {
  meetingId: string;
  onClose?: () => void;
  closeLabel?: string;
  /** "3 of 12" — omitted when the detail is not being read inside a list. */
  position?: string;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  const tier = useTier();
  const showRail = isProTier(tier);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['meeting', meetingId],
    queryFn: () => fetchMeetingDetail(meetingId),
  });

  const explain = useMutation({ mutationFn: () => explainMeeting(meetingId) });
  const [openCitation, setOpenCitation] = useState<Citation | null>(null);
  const simplified = explain.data?.text;

  // J / K to step through the list, Escape to close — the keyboard affordances the design
  // advertises in its own hint text, so they have to actually work.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (e.key === 'Escape' && onClose) onClose();
      if (e.key === 'j' && onNext) onNext();
      if (e.key === 'k' && onPrev) onPrev();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onNext, onPrev]);

  if (isLoading) return <div className="meeting__loading">Loading meeting…</div>;
  if (isError || !data) return <div className="meeting__loading">Couldn’t load this meeting.</div>;

  return (
    <article className="meeting">
      <header className="meeting__bar">
        {onClose && (
          <button type="button" className="meeting__close" onClick={onClose}>
            <Icon name="chevron-left" /> {closeLabel}
          </button>
        )}
        {position && (
          <span className="meeting__position">
            {position}
            <span className="meeting__hint"> · J / K to move · Esc to close</span>
          </span>
        )}
        {(onPrev || onNext) && (
          <span className="meeting__steps">
            <button type="button" onClick={onPrev} disabled={!onPrev} aria-label="Previous meeting">
              <Icon name="chevron-left" />
            </button>
            <button type="button" onClick={onNext} disabled={!onNext} aria-label="Next meeting">
              <Icon name="chevron-right" />
            </button>
          </span>
        )}
      </header>

      <div className="meeting__panes" data-rail={showRail}>
        <div className="meeting__article">
          <div className="meeting__head">
            <BodyBadge body={data.body} />
            <span className="meeting__where">
              {data.townName}, {data.townRegion} · {data.bodyName}
            </span>
          </div>
          <p className="meeting__stamp">
            {data.date} · processed {data.ago}
          </p>

          <h1 className="meeting__title">{data.title}</h1>

          {/* The meeting this summary came from, and its category. Context for the heading —
              the heading names the part, not the session. */}
          <p className="meeting__context">
            {data.meetingTitle}
            {data.category && <> · {data.category}</>}
          </p>

          {simplified ? (
            <>
              <p className="meeting__explainBadge">
                <Icon name="zap" /> Aware Explain · simplified
              </p>
              <p className="meeting__para">{simplified}</p>
            </>
          ) : (
            <>
              {/* Both summaries, labelled. They are two different pieces of writing from the
                  corpus, not one truncated into the other, so the screen says which is which. */}
              {data.summary && (
                <>
                  <h2 className="meeting__sectionTitle">Short Summary</h2>
                  <p className="meeting__para">{data.summary}</p>
                </>
              )}
              {data.paragraphs.length > 0 && (
                <>
                  <h2 className="meeting__sectionTitle">Long Summary</h2>
                  {data.paragraphs.map((para, i) => (
                    <p key={i} className="meeting__para">
                      {para}
                    </p>
                  ))}
                </>
              )}
            </>
          )}

          <div className="meeting__actions">
            {simplified ? (
              <button type="button" className="meeting__btn" onClick={() => explain.reset()}>
                Show original
              </button>
            ) : (
              <button
                type="button"
                className="meeting__btn meeting__btn--primary"
                onClick={() => explain.mutate()}
                disabled={explain.isPending}
              >
                <Icon name="zap" />
                {explain.isPending ? 'Simplifying…' : 'Aware Explain'}
              </button>
            )}

            {data.num &&
              (data.inRecord ? (
                <Link to="/legislative-history" className="meeting__btn meeting__btn--quiet">
                  In this record
                </Link>
              ) : can(tier, 'legislativeHistory') ? (
                <button type="button" className="meeting__btn">
                  Track {data.num}
                </button>
              ) : (
                <Link to="/pricing" className="meeting__btn meeting__btn--locked">
                  <Icon name="lock" /> Follow {data.num}
                </Link>
              ))}
          </div>

          {!simplified && data.quote && (
            <blockquote className="meeting__quote">
              <p>“{data.quote.text}”</p>
              <footer>
                — {data.quote.attribution}
                <Cite citation={data.quote.citation} onOpen={setOpenCitation} inline />
              </footer>
            </blockquote>
          )}
        </div>

        {showRail && <MeetingRail data={data} onOpenCitation={setOpenCitation} />}
      </div>

      {openCitation && (
        <TranscriptPanel
          transcript={data.transcript}
          citation={openCitation}
          meetingTitle={data.title}
          onClose={() => setOpenCitation(null)}
        />
      )}
    </article>
  );
}

/**
 * A citation control. Opens the transcript at the sentence this fact came from.
 *
 * Rendered as a button, not a link — it opens a panel over the article rather than navigating
 * away from what the reader is checking.
 */
function Cite({
  citation,
  onOpen,
  inline = false,
}: {
  citation: Citation | null;
  onOpen: (citation: Citation) => void;
  inline?: boolean;
}) {
  if (!citation) return null;
  return (
    <button
      type="button"
      className={inline ? 'meeting__cite meeting__cite--inline' : 'meeting__cite'}
      title={citation.quote}
      onClick={() => onOpen(citation)}
    >
      <Icon name="quote" /> Source
    </button>
  );
}

function MeetingRail({
  data,
  onOpenCitation,
}: {
  data: MeetingDetailData;
  onOpenCitation: (citation: Citation) => void;
}) {
  return (
    <aside className="meeting__rail" aria-label="Extracted from this meeting">
      {data.votes.length > 0 && (
        <section className="meeting__section">
          <h2 className="u-meta">Votes</h2>
          <ul className="meeting__list">
            {data.votes.map((vote, i) => (
              <li key={i} className="meeting__vote">
                <span className="meeting__voteItem">{vote.item}</span>
                <span className="meeting__voteRow">
                  <span className="meeting__outcome" data-outcome={vote.outcome}>
                    {vote.outcome}
                  </span>
                  {vote.tally && <span className="meeting__tally">{vote.tally}</span>}
                  <Cite citation={vote.citation} onOpen={onOpenCitation} />
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.dollarFigures.length > 0 && (
        <section className="meeting__section">
          <h2 className="u-meta">Dollar figures</h2>
          <ul className="meeting__list">
            {data.dollarFigures.map((figure, i) => (
              <li key={i} className="meeting__dollar">
                <span className="meeting__amount">{figure.amount}</span>
                <span className="meeting__dollarLabel">{figure.label}</span>
                <Cite citation={figure.citation} onOpen={onOpenCitation} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.namedInMeeting.length > 0 && (
        <section className="meeting__section">
          <h2 className="u-meta">Named in this meeting</h2>
          {/* Plain text, deliberately not links. The Entities surface was removed — the
              extractor covers civic-role people and permanent places only, so there is no
              page behind a name to link to. */}
          <ul className="meeting__named">
            {data.namedInMeeting.map((thing) => (
              <li key={thing.name}>{thing.name}</li>
            ))}
          </ul>
        </section>
      )}
    </aside>
  );
}
